import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiZap, FiCheck, FiAlertCircle, FiPlus, FiMaximize2, FiArrowRight } = FiIcons;

const SmartSuggestionModal = ({ isOpen, onClose, onAccept, onDecline, suggestedFoods = [], type = 'add', adjustments = [] }) => {
  if (!isOpen) return null;

  const isRangeUpdate = type === 'range';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]"
      onClick={onDecline}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`p-6 text-white ${isRangeUpdate ? 'bg-gradient-to-r from-blue-600 to-indigo-600' : 'bg-gradient-to-r from-indigo-600 to-purple-600'}`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-full">
                <SafeIcon icon={isRangeUpdate ? FiMaximize2 : FiZap} className="text-2xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{isRangeUpdate ? 'Extend Ranges?' : 'Smart Suggestion'}</h2>
                <p className="text-blue-100 text-sm">{isRangeUpdate ? 'Optimization Helper' : 'Missing Macros Detected'}</p>
              </div>
            </div>
            <button 
              onClick={onDecline}
              className="text-white/70 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className={`flex items-start gap-3 mb-6 p-4 rounded-lg border ${isRangeUpdate ? 'bg-blue-50 border-blue-100 text-blue-800' : 'bg-orange-50 border-orange-100 text-gray-600'}`}>
            <SafeIcon icon={FiAlertCircle} className={`text-xl flex-shrink-0 mt-0.5 ${isRangeUpdate ? 'text-blue-500' : 'text-orange-500'}`} />
            <p className="text-sm">
              {isRangeUpdate 
                ? "We found a solution, but it requires extending the serving ranges for some of your selected foods."
                : "Your current food selection makes it mathematically difficult to hit your macro targets. We recommend adding the following foods to bridge the gap:"
              }
            </p>
          </div>

          {isRangeUpdate ? (
            <>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <SafeIcon icon={FiMaximize2} className="text-blue-600" />
                Suggested Adjustments:
              </h3>
              <div className="space-y-3 mb-8">
                {adjustments.map((adj, index) => (
                  <div key={`${adj.food.id}-${index}`} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <div className="font-medium text-gray-800 mb-2">{adj.food.name}</div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="bg-white px-2 py-1 rounded border border-gray-200 text-gray-500">
                        Max: {adj.oldMax} {adj.unit}
                      </div>
                      <SafeIcon icon={FiArrowRight} className="text-gray-400" />
                      <div className="bg-blue-100 px-2 py-1 rounded border border-blue-200 text-blue-700 font-semibold">
                        Max: {adj.newMax} {adj.unit}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <SafeIcon icon={FiPlus} className="text-indigo-600" />
                Suggested Additions:
              </h3>
              <div className="space-y-3 mb-8">
                {suggestedFoods.map((food, index) => (
                  <div key={`${food.id}-${index}`} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <div>
                      <div className="font-medium text-gray-800">{food.name}</div>
                      <div className="text-xs text-gray-500">{food.category}</div>
                    </div>
                    <div className="text-sm font-semibold text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                      {food.desiredMin}-{food.desiredMax} {food.selectedServingUnit}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="flex gap-3">
            <button
              onClick={onDecline}
              className="flex-1 py-3 px-4 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              No, Keep Limits
            </button>
            <button
              onClick={onAccept}
              className={`flex-1 py-3 px-4 text-white rounded-xl font-medium hover:opacity-90 transition-all shadow-md flex items-center justify-center gap-2 ${isRangeUpdate ? 'bg-blue-600' : 'bg-indigo-600'}`}
            >
              <SafeIcon icon={FiCheck} />
              {isRangeUpdate ? 'Update Ranges' : 'Accept & Add'}
            </button>
          </div>
          
          <p className="text-xs text-center text-gray-400 mt-4">
            You can disable these suggestions in your profile settings.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SmartSuggestionModal;